// Plan and billing of the organisation: my plan, monthly use and management. A free account chooses a plan
// and pays on a Stripe page; a paid one manages the subscription in the Stripe portal. The plan and quota
// follow the signed-in person (the same balance preparation uses). The invoices are not issued here.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type Billing,
  type BillingInterval,
  billingQuery,
  confirmCheckout,
  meQuery,
  openBillingPortal,
  type PaidPlan,
  type Plan,
  type PlanDetails,
  planQuery,
  type Subscription,
  startCheckout,
} from '../api';
import { Button, Choice, Message, Page, WebsiteLink } from '../ui';
import { publicPage } from '../util';
import { OrganizationNav } from './org-nav';

/** Owners and admins may change the plan or ask about billing; editors and viewers only consult. */
function canManageBilling(role: string | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

function PlanCard({ data, language }: { data: PlanDetails; language: string }) {
  const { t } = useTranslation();
  const extras = t(`billing.features.${data.plan}`, { returnObjects: true });
  const extraList = Array.isArray(extras) ? (extras as string[]) : [];

  return (
    <section aria-labelledby="mi-plan" className="billing-card">
      <h2 id="mi-plan" className="billing-card-label">
        {t('billing.myPlan')}
      </h2>
      <p className="billing-plan-name">{t('billing.planName', { plan: t(`users.plans.${data.plan}`) })}</p>
      <p className="billing-plan-summary">{t(`billing.summaries.${data.plan}`)}</p>

      <h3 className="billing-includes-title">{t('billing.includes')}</h3>
      <dl className="billing-includes">
        <div>
          <dt>{t('billing.pagesLimit')}</dt>
          <dd>
            {data.limits.pagesPerMonth === null
              ? t('billing.pagesUnlimited')
              : t('billing.pagesPerMonth', { count: data.limits.pagesPerMonth })}
          </dd>
        </div>
        <div>
          <dt>{t('billing.wpLimit')}</dt>
          <dd>
            {data.limits.wpSites === null
              ? t('billing.wpUnlimited')
              : t('billing.wpSites', { count: data.limits.wpSites })}
          </dd>
        </div>
        <div>
          <dt>{t('billing.selectPages')}</dt>
          <dd>{data.limits.canSelectPages ? t('billing.selectPagesYes') : t('billing.selectPagesNo')}</dd>
        </div>
      </dl>

      {extraList.length > 0 && (
        <ul className="billing-extras">
          {extraList.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}

      <p className="billing-card-action">
        <WebsiteLink href={publicPage(language, 'pricing')}>{t('billing.seeOtherPlans')}</WebsiteLink>
      </p>
    </section>
  );
}

function UsageCard({ data, language }: { data: PlanDetails; language: string }) {
  const { t } = useTranslation();
  const month = new Intl.DateTimeFormat(language, { month: 'long', timeZone: 'UTC' }).format(new Date());
  const resets = new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(data.usage.renewsAt));
  const unlimited = data.usage.limit === null || data.usage.remaining === null;
  const consumed = Math.min(data.usage.used + data.usage.reserved, data.usage.limit ?? 0);

  return (
    <section aria-labelledby="uso-mensual" className="billing-card">
      <h2 id="uso-mensual" className="billing-card-label">
        {t('billing.usageOfMonth', { month })}
      </h2>
      {unlimited ? (
        <>
          <p className="billing-usage-figure">{t('billing.pagesAnalysed', { count: data.usage.used })}</p>
          <p>{t('billing.monthlyQuotaUnlimited')}</p>
        </>
      ) : (
        <>
          <p className="billing-usage-figure">
            {t('billing.pagesUsedOf', { used: data.usage.used, limit: data.usage.limit })}
          </p>
          <progress
            className="wp-meter billing-meter"
            max={data.usage.limit!}
            value={consumed}
            aria-label={t('billing.usageLabel')}
          >
            {consumed}/{data.usage.limit}
          </progress>
          <p className="font-bold">{t('billing.pagesAvailable', { count: data.usage.remaining })}</p>
          {data.usage.reserved > 0 && (
            <p className="text-sm text-ink-muted">
              {t('billing.usageReserved', { count: data.usage.reserved })}
            </p>
          )}
        </>
      )}
      <p className="text-sm text-ink-muted">{t('billing.usageHint')}</p>
      <p className="billing-reset text-sm text-ink-muted">{t('billing.quotaResets', { date: resets })}</p>
    </section>
  );
}

const PAID_PLANS: PaidPlan[] = ['pro', 'agencia'];

/** Sends the browser to a page of Stripe: only secure addresses, never anything else the answer might carry. */
function goTo(url: string) {
  if (new URL(url).protocol === 'https:') window.location.assign(url);
}

function useMoney(language: string) {
  return (cents: number) =>
    new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
}

const dateOf = (iso: string, language: string) =>
  new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));

/** Free accounts: the plans that can be bought, by the period the person prefers. */
function PurchaseCard({ billing, language }: { billing: Billing; language: string }) {
  const { t } = useTranslation();
  const money = useMoney(language);
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [busy, setBusy] = useState<PaidPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sending = useRef(false);

  async function buy(plan: PaidPlan) {
    if (sending.current) return;
    sending.current = true;
    setBusy(plan);
    setError(null);
    try {
      goTo((await startCheckout(plan, interval)).url);
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : null;
      setError(
        code && ['suscripcion-activa', 'plan-asignado', 'limite', 'permisos'].includes(code)
          ? t(`billing.purchase.errors.${code}`)
          : t('billing.purchase.errors.failed'),
      );
      // On success the page is replaced by Stripe's; only a failure needs the button back.
      sending.current = false;
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby="elige-plan" className="billing-card billing-card-wide">
      <h2 id="elige-plan" className="text-xl font-bold">
        {t('billing.purchase.title')}
      </h2>
      <p>{t('billing.purchase.intro')}</p>
      <Choice
        name="billing-interval"
        label={t('billing.purchase.period')}
        value={interval}
        onChange={setInterval}
        options={[
          { value: 'month', label: t('billing.purchase.month') },
          { value: 'year', label: t('billing.purchase.year'), hint: t('billing.purchase.yearHint') },
        ]}
      />
      <ul className="billing-offers">
        {PAID_PLANS.map((plan) => {
          const price = billing.prices[plan][interval];
          const name = t(`users.plans.${plan}`);
          return (
            <li key={plan} className="billing-offer">
              <h3 className="text-lg font-bold">{name}</h3>
              <p className="billing-offer-price">
                {t(interval === 'month' ? 'billing.purchase.perMonth' : 'billing.purchase.perYear', {
                  price: money(price),
                })}
              </p>
              {interval === 'year' && (
                <p className="text-sm text-ink-muted">
                  {t('billing.purchase.equivalent', { price: money(price / 12) })}
                </p>
              )}
              <p className="text-ink-muted">{t(`billing.summaries.${plan}`)}</p>
              <Button className="mt-auto" isDisabled={busy !== null} onPress={() => void buy(plan)}>
                {busy === plan
                  ? t('billing.purchase.redirecting')
                  : t('billing.purchase.buy', { plan: name })}
              </Button>
            </li>
          );
        })}
      </ul>
      {error && <Message tone="error">{error}</Message>}
      <p className="text-sm text-ink-muted">{t('billing.purchase.secure')}</p>
      <p className="text-sm">
        <WebsiteLink href={publicPage(language, 'contact')}>{t('billing.purchase.custom')}</WebsiteLink>
      </p>
    </section>
  );
}

/** Paid accounts: what they pay and when it renews, and the door to the Stripe portal. */
function SubscriptionCard({
  subscription,
  billing,
  language,
}: {
  subscription: Subscription;
  billing: Billing;
  language: string;
}) {
  const { t } = useTranslation();
  const money = useMoney(language);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const sending = useRef(false);
  const plan = t(`users.plans.${subscription.plan}`);
  const end = subscription.currentPeriodEnd ? dateOf(subscription.currentPeriodEnd, language) : null;

  async function manage() {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setError(false);
    try {
      goTo((await openBillingPortal()).url);
    } catch {
      setError(true);
      sending.current = false;
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="tu-suscripcion" className="billing-card billing-card-wide">
      <h2 id="tu-suscripcion" className="text-xl font-bold">
        {t('billing.subscription.title')}
      </h2>
      <p>
        {t('billing.subscription.line', {
          plan,
          period: t(`billing.subscription.${subscription.interval}`),
          price: t(
            subscription.interval === 'month' ? 'billing.purchase.perMonth' : 'billing.purchase.perYear',
            {
              price: money(billing.prices[subscription.plan][subscription.interval]),
            },
          ),
        })}
      </p>
      {subscription.status === 'past_due' && (
        <Message tone="error">{t('billing.subscription.pastDue')}</Message>
      )}
      {end &&
        (subscription.cancelAtPeriodEnd ? (
          <p className="font-bold">{t('billing.subscription.cancels', { plan, date: end })}</p>
        ) : (
          <p>{t('billing.subscription.renews', { date: end })}</p>
        ))}
      <p className="text-ink-muted">{t('billing.subscription.manageHint')}</p>
      <div>
        <Button isDisabled={busy} onPress={() => void manage()}>
          {busy ? t('billing.purchase.redirecting') : t('billing.subscription.manage')}
        </Button>
      </div>
      {error && <Message tone="error">{t('billing.subscription.portalFailed')}</Message>}
    </section>
  );
}

/** Before the payments are open, or for people who cannot manage the plan: where to read about the plans. */
function ManagementCard({ manage, plan, language }: { manage: boolean; plan: Plan; language: string }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="gestion-facturacion" className="billing-card billing-card-wide">
      <h2 id="gestion-facturacion" className="text-xl font-bold">
        {t('billing.managementTitle')}
      </h2>
      {manage ? (
        <>
          <p>{t(plan === 'gratis' ? 'billing.managementFree' : 'billing.managementPaid')}</p>
          <div className="flex flex-wrap items-center gap-4">
            <WebsiteLink href={publicPage(language, 'pricing')}>{t('billing.seePlans')}</WebsiteLink>
            <WebsiteLink href={publicPage(language, 'contact')}>{t('billing.contactSupport')}</WebsiteLink>
          </div>
        </>
      ) : (
        <>
          <p>{t('billing.readOnly')}</p>
          <WebsiteLink href={publicPage(language, 'pricing')}>{t('billing.seePlans')}</WebsiteLink>
        </>
      )}
    </section>
  );
}

/**
 * What the page says about the payment the person has just made or left: Stripe sends them back here. The
 * payment is confirmed with Stripe right away (the notice by webhook may take a few seconds more).
 */
function useReturnFromStripe() {
  const { pago, session } = useSearch({ from: '/organization/billing' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'confirming' | 'paid' | 'cancelled'>(
    pago === 'cancelado' ? 'cancelled' : pago === 'ok' ? 'confirming' : 'idle',
  );
  const confirmed = useRef(false);

  useEffect(() => {
    if (pago === 'ok' && session && !confirmed.current) {
      confirmed.current = true;
      void confirmCheckout(session)
        .then(({ subscription }) => setState(subscription ? 'paid' : 'confirming'))
        .catch(() => setState('confirming'))
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: ['billing'] });
          void queryClient.invalidateQueries({ queryKey: ['plan'] });
          void queryClient.invalidateQueries({ queryKey: ['me'] });
        });
    }
    // The address no longer carries the payment, so reloading the page does not repeat it.
    if (pago) void navigate({ to: '/organization/billing', search: {}, replace: true });
  }, [pago, session, navigate, queryClient]);

  return state;
}

export function BillingPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const returned = useReturnFromStripe();
  const plan = useQuery(planQuery);
  // While the payment is being confirmed, ask again every few seconds.
  const billing = useQuery({
    ...billingQuery,
    refetchInterval: (query) => (returned === 'confirming' && !query.state.data?.subscription ? 3000 : false),
  });
  if (!me?.organization) return null;

  const manage = canManageBilling(me.organization.role);
  const offers = billing.data;
  const subscription = offers?.subscription ?? null;

  return (
    <Page title={t('billing.title')} wide>
      <OrganizationNav />

      {returned === 'paid' && subscription && (
        <Message tone="ok">
          {t('billing.return.paid', { plan: t(`users.plans.${subscription.plan}`) })}
        </Message>
      )}
      {returned === 'confirming' && !subscription && (
        <Message tone="ok">{t('billing.return.confirming')}</Message>
      )}
      {returned === 'cancelled' && <Message tone="ok">{t('billing.return.cancelled')}</Message>}

      {plan.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : plan.isError || !plan.data ? (
        <div className="flex flex-col items-start gap-3">
          <Message tone="error">{t('billing.loadFailed')}</Message>
          <Button onPress={() => void plan.refetch()}>{t('app.retry')}</Button>
        </div>
      ) : (
        <div className="billing-layout">
          <div className="billing-grid">
            <PlanCard data={plan.data} language={i18n.language} />
            <UsageCard data={plan.data} language={i18n.language} />
          </div>
          {billing.isPending ? null : !offers || !manage ? (
            <ManagementCard manage={manage} plan={plan.data.plan} language={i18n.language} />
          ) : subscription ? (
            <SubscriptionCard subscription={subscription} billing={offers} language={i18n.language} />
          ) : offers.canSubscribe ? (
            <PurchaseCard billing={offers} language={i18n.language} />
          ) : (
            <section aria-labelledby="plan-asignado" className="billing-card billing-card-wide">
              <h2 id="plan-asignado" className="text-xl font-bold">
                {t('billing.managementTitle')}
              </h2>
              <p>{t('billing.assigned')}</p>
              <WebsiteLink href={publicPage(i18n.language, 'contact')}>
                {t('billing.contactSupport')}
              </WebsiteLink>
            </section>
          )}
        </div>
      )}
    </Page>
  );
}
